import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import http from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Loader2, Search, Award, Users, Sparkles, Star, Ticket, ShoppingBag, Gavel, HeartHandshake, ChevronRight, ChevronLeft, Lock, Mail, Copy, QrCode, Download } from 'lucide-react';

const CATEGORY_ICONS = {
  engagement: Star, events: Ticket, purchases: ShoppingBag,
  bidding: Gavel, donations: HeartHandshake, referrals: Users,
};

/** Admin-only: browse every user's platform loyalty card, history, and
 *  referral tree. Also lets admins jump into per-event QR management. */
export default function AdminLoyalty() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null); // full detail
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [events, setEvents] = useState([]);
  const [evTab, setEvTab] = useState('users');
  const [eventQr, setEventQr] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [loadingRefunds, setLoadingRefunds] = useState(false);
  const [refunding, setRefunding] = useState('');

  const loadUsers = (query = '') => {
    setLoading(true);
    http.get('/admin/loyalty/users', { params: { q: query, limit: 100 } })
      .then(r => setRows(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  const loadEvents = () => http.get('/events').then(r => setEvents(r.data)).catch(() => {});
  const loadRefunds = () => {
    setLoadingRefunds(true);
    http.get('/admin/refunds/eligible').then(r => setRefunds(r.data)).catch(() => {}).finally(() => setLoadingRefunds(false));
  };

  useEffect(() => { if (user?.isAdmin) { loadUsers(); loadEvents(); loadRefunds(); } else { setLoading(false); } }, [user]);

  const doRefund = async (pid) => {
    if (!window.confirm('Refund this payment? Reverses PayPal capture + points + stamps.')) return;
    setRefunding(pid);
    try {
      const r = await http.post(`/admin/refunds/${pid}`);
      toast({ title: 'Refund issued', description: `${r.data.reversal?.pointsBack || 0} pts + 1 stamp reversed` });
      loadRefunds();
    } catch (e) { toast({ title: 'Refund failed', description: e?.response?.data?.detail || 'Try again' }); }
    finally { setRefunding(''); }
  };

  if (!user?.isAdmin) return (
    <div className="max-w-xl mx-auto pbb-card p-8 text-center">
      <Lock className="mx-auto text-green-400" size={26}/>
      <div className="mt-3 font-semibold">Admin Access Only</div>
      <p className="text-sm text-gray-400 mt-1">Ask the PBB team to be granted admin access.</p>
    </div>
  );

  const openUser = async (uid) => {
    setLoadingDetail(true);
    try {
      const r = await http.get(`/admin/loyalty/user/${uid}`);
      setSelected(r.data);
    } finally { setLoadingDetail(false); }
  };

  const openEventQr = async (eid) => {
    try {
      const r = await http.get(`/admin/events/${eid}/qr`);
      setEventQr(r.data);
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' });
    }
  };

  const setEventLocation = async (eid) => {
    const addr = window.prompt('Enter the venue address (auto-geocoded via Photon):', '');
    if (!addr) return;
    try {
      await http.post(`/admin/events/${eid}/location`, { address: addr });
      toast({ title: 'Location updated' });
      openEventQr(eid);
      loadEvents();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const copyText = async (t) => { try { await navigator.clipboard.writeText(t); toast({ title: 'Copied' }); } catch {} };

  const exportCsv = () => {
    if (!rows.length) { toast({ title: 'Nothing to export' }); return; }
    const headers = ['id','name','email','points','stamps','completions','bonusEarned','referralCode','referredBy','referralEarned','createdAt'];
    const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [headers.join(',')].concat(rows.map(r => headers.map(h => esc(r[h])).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pbb-loyalty-users-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast({ title: 'CSV downloaded', description: `${rows.length} users` });
  };

  // ---- USER DETAIL VIEW ----
  if (selected) {
    const u = selected.user;
    return (
      <div className="space-y-4" data-testid="admin-user-detail">
        <button onClick={() => setSelected(null)} className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white"><ChevronLeft size={16}/> Back to users</button>
        <div className="pbb-card p-5">
          <div className="flex items-center gap-3">
            <img src={u.avatar} alt="" className="w-14 h-14 rounded-full object-cover"/>
            <div className="flex-1">
              <div className="font-semibold text-lg">{u.name}</div>
              <div className="text-xs text-gray-400">{u.email} · joined {u.createdAt?.slice(0,10)}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-400">{u.points.toLocaleString()}</div>
              <div className="text-[11px] text-gray-500">balance</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <div className="pbb-card p-3">
              <div className="text-[10px] uppercase text-gray-500">Card progress</div>
              <div className="text-2xl font-bold">{u.stamps}<span className="text-sm text-gray-400"> / {u.target}</span></div>
              <div className="grid grid-cols-10 gap-0.5 mt-2">
                {Array.from({ length: u.target }).map((_, i) => (
                  <div key={i} className={`h-3 rounded-sm ${i < u.stamps ? 'bg-green-500' : 'bg-[#1a2a20] border border-dashed border-green-600/40'}`}/>
                ))}
              </div>
            </div>
            <div className="pbb-card p-3">
              <div className="text-[10px] uppercase text-gray-500">Card completions</div>
              <div className="text-2xl font-bold">{u.completions}</div>
              <div className="text-[11px] text-green-400">+{u.bonusEarned} pts bonus earned</div>
            </div>
            <div className="pbb-card p-3">
              <div className="text-[10px] uppercase text-gray-500">Referrals</div>
              <div className="text-2xl font-bold">{selected.referrals.length}</div>
              <div className="text-[11px] text-green-400">+{u.referralEarned} pts earned</div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4 text-[11px]">
            {Object.entries(u.byCategory).map(([k, v]) => {
              const Icon = CATEGORY_ICONS[k];
              return (
                <div key={k} className="rounded-lg bg-[#0d1f14] border border-[#153b23] px-2 py-1.5 inline-flex items-center gap-1.5">
                  <Icon size={12} className="text-green-400"/>
                  <span className="text-gray-400 capitalize truncate">{k}</span>
                  <b className="text-green-400 ml-auto">{v}</b>
                </div>
              );
            })}
          </div>
        </div>

        {/* Referral tree */}
        <div className="pbb-card p-5">
          <div className="font-semibold mb-2 inline-flex items-center gap-2"><Users size={14} className="text-green-400"/> Referral network</div>
          {selected.referrer && (
            <div className="text-xs text-gray-400 mb-2">Referred by: <b className="text-white">{selected.referrer.name}</b> ({selected.referrer.email})</div>
          )}
          <div className="text-xs text-gray-400 mb-2">Referral code <span className="font-mono text-green-400">{u.referralCode}</span></div>
          {selected.referrals.length === 0 ? (
            <div className="text-sm text-gray-500">This user hasn't referred anyone yet.</div>
          ) : (
            <ul className="divide-y divide-[#1f1f1f]" data-testid="referral-list">
              {selected.referrals.map(r => (
                <li key={r.id} className="py-2 flex items-center gap-3 text-sm">
                  <div className="flex-1 truncate">
                    <div className="text-white truncate">{r.name}</div>
                    <div className="text-[11px] text-gray-500 truncate">{r.email}</div>
                  </div>
                  <div className="text-right text-[11px]">
                    <div><b>{r.platform_completions || 0}</b> completions</div>
                    <div className="text-green-400">+{r.platform_bonus_earned || 0} pts</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* History */}
        <div className="pbb-card p-5">
          <div className="font-semibold mb-3 inline-flex items-center gap-2"><Sparkles size={14} className="text-green-400"/> Recent activity ({selected.history.length})</div>
          {selected.history.length === 0 ? (
            <div className="text-sm text-gray-500">No loyalty activity yet.</div>
          ) : (
            <ul className="space-y-1.5 text-xs" data-testid="user-history">
              {selected.history.slice(0, 50).map(h => (
                <li key={h.id} className="flex items-center gap-2 py-1 border-b border-[#1f1f1f]">
                  <span className="text-gray-500 w-32 truncate">{new Date(h.createdAt).toLocaleString()}</span>
                  <span className="text-gray-300 capitalize w-24">{h.category}</span>
                  <span className="text-gray-500 flex-1 truncate">{h.source}</span>
                  <span className="text-green-400 w-16 text-right">+{h.stamps || 0}★</span>
                  <span className="text-green-400 w-20 text-right">+{h.creditPoints || 0} pts</span>
                  {h.bonusCredited > 0 && <span className="text-yellow-400 w-24 text-right">🎁 +{h.bonusCredited}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // ---- LIST VIEW ----
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold inline-flex items-center gap-2"><Award className="text-green-400" size={22}/> Admin · Loyalty & Events</h1>
        <p className="text-gray-400 text-sm">Browse every user's platform loyalty card, referrals, and history. Set event locations & get QR codes.</p>
      </div>

      <div className="flex gap-2 border-b border-[#1f1f1f]">
        {[['users','Users'],['events','Event QR codes'],['refunds','Refunds']].map(([k,l]) => (
          <button key={k} onClick={() => setEvTab(k)} className={`px-3 py-2 text-sm border-b-2 -mb-px ${evTab === k ? 'border-green-500 text-white' : 'border-transparent text-gray-400'}`} data-testid={`admin-tab-${k}`}>{l}{k === 'refunds' && refunds.length > 0 && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/40">{refunds.length}</span>}</button>
        ))}
      </div>

      {evTab === 'users' && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative max-w-sm flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
              <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadUsers(q)} placeholder="Search name, email, or referral code…" className="pbb-input pl-10" data-testid="admin-users-search"/>
            </div>
            <button onClick={exportCsv} data-testid="admin-users-export" disabled={!rows.length} className="px-3 py-2.5 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-sm inline-flex items-center gap-1 disabled:opacity-50"><Download size={14}/> Export CSV</button>
          </div>
          {loading ? <Loader2 className="animate-spin text-gray-400" size={16}/> : (
            <div className="pbb-card overflow-hidden" data-testid="admin-users-table">
              <table className="w-full text-sm">
                <thead className="bg-[#0d1f14] text-[11px] uppercase text-gray-400">
                  <tr>
                    <th className="text-left px-3 py-2">User</th>
                    <th className="text-left px-3 py-2">Points</th>
                    <th className="text-left px-3 py-2">Card</th>
                    <th className="text-left px-3 py-2">Completions</th>
                    <th className="text-left px-3 py-2">Bonus</th>
                    <th className="text-left px-3 py-2">Referral</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1f1f1f]">
                  {rows.map(r => (
                    <tr key={r.id} className="hover:bg-[#0d1f14] cursor-pointer" onClick={() => openUser(r.id)} data-testid={`admin-user-${r.id}`}>
                      <td className="px-3 py-2 flex items-center gap-2 min-w-0">
                        <img src={r.avatar} alt="" className="w-7 h-7 rounded-full object-cover shrink-0"/>
                        <div className="min-w-0">
                          <div className="text-white truncate">{r.name}</div>
                          <div className="text-[11px] text-gray-500 truncate">{r.email}</div>
                        </div>
                      </td>
                      <td className="px-3 py-2">{r.points?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-green-400">{r.stamps}/10</td>
                      <td className="px-3 py-2">{r.completions}</td>
                      <td className="px-3 py-2 text-green-400">+{r.bonusEarned}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{r.referralCode}</td>
                      <td className="px-3 py-2"><ChevronRight size={14} className="text-gray-500"/></td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={7} className="text-center text-gray-500 text-sm px-3 py-6">No users.</td></tr>
                  )}
                </tbody>
              </table>
              {loadingDetail && <div className="p-3 text-xs text-gray-400 inline-flex items-center gap-2"><Loader2 className="animate-spin" size={12}/> Loading detail…</div>}
            </div>
          )}
        </>
      )}

      {evTab === 'events' && (
        <div className="pbb-card p-4 space-y-3" data-testid="admin-events">
          <div className="text-xs text-gray-400">Set a venue address per event to auto-geocode coordinates and generate a location-locked check-in QR (+100 pts once per event, within 500 m).</div>
          <ul className="divide-y divide-[#1f1f1f]">
            {events.map(ev => (
              <li key={ev.id} className="py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{ev.title}</div>
                  <div className="text-[11px] text-gray-500 truncate">{ev.city} · {ev.date} · {ev.lat != null ? `📍 ${ev.lat.toFixed(4)},${ev.lng.toFixed(4)}` : '⚠️ no location'}</div>
                </div>
                <button onClick={() => setEventLocation(ev.id)} data-testid={`event-loc-${ev.id}`} className="px-2 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-xs">Set location</button>
                <button onClick={() => openEventQr(ev.id)} data-testid={`event-qr-${ev.id}`} className="px-2 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs inline-flex items-center gap-1"><QrCode size={12}/> QR</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {evTab === 'refunds' && (
        <div className="pbb-card p-4 space-y-3" data-testid="admin-refunds">
          <div className="text-xs text-gray-400">Refund a captured PayPal payment. Reverses the cash via PayPal AND rolls back +100 pts + 1 loyalty stamp (including any card-completion bonus if the purchase crossed the 10-stamp threshold).</div>
          {loadingRefunds ? <Loader2 className="animate-spin text-gray-400" size={16}/> : refunds.length === 0 ? (
            <div className="text-sm text-gray-500 py-3">No refundable payments right now.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#0d1f14] text-[11px] uppercase text-gray-400">
                <tr>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Kind</th>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-left px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">When</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1f1f1f]">
                {refunds.map(r => (
                  <tr key={r.id} data-testid={`refund-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="text-white truncate">{r.userName}</div>
                      <div className="text-[11px] text-gray-500 truncate">{r.userEmail}</div>
                    </td>
                    <td className="px-3 py-2 capitalize">{(r.kind || '').replace('_',' ')}</td>
                    <td className="px-3 py-2 truncate max-w-[180px]">{r.itemTitle || r.auctionId || '—'}</td>
                    <td className="px-3 py-2 text-green-400">${r.amountUsd}</td>
                    <td className="px-3 py-2 text-[11px] text-gray-400">{new Date(r.createdAt).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => doRefund(r.id)} disabled={refunding === r.id} data-testid={`refund-btn-${r.id}`} className="px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 text-xs disabled:opacity-50">
                        {refunding === r.id ? 'Refunding…' : 'Refund'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}


      {/* Event QR modal */}
      {eventQr && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEventQr(null)}>
          <div className="pbb-card w-full max-w-md p-5" onClick={e => e.stopPropagation()} data-testid="event-qr-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-lg inline-flex items-center gap-2"><Ticket size={18} className="text-green-400"/> {eventQr.title}</div>
              <button onClick={() => setEventQr(null)} className="text-gray-400">✕</button>
            </div>
            {!eventQr.hasLocation ? (
              <div className="text-sm text-yellow-400">This event has no location yet. Click "Set location" first.</div>
            ) : (
              <>
                <div className="rounded-xl bg-white p-4 flex items-center justify-center">
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&data=${encodeURIComponent(`${window.location.origin}${eventQr.checkinPath}`)}`} alt="Event QR" className="w-64 h-64"/>
                </div>
                <div className="mt-3 text-sm">
                  <div className="text-gray-400 text-xs">Scan → verify location → +100 pts</div>
                  <div className="mt-2 text-[11px] text-gray-500 break-all bg-[#0d1f14] border border-[#153b23] rounded px-2 py-1.5">{`${window.location.origin}${eventQr.checkinPath}`}</div>
                  <div className="text-[11px] text-green-400 mt-2">{eventQr.attendedCount} attendees checked in</div>
                </div>
                <button onClick={() => copyText(`${window.location.origin}${eventQr.checkinPath}`)} className="mt-3 w-full px-3 py-2 rounded-lg border border-[#2a2a2a] text-sm inline-flex items-center justify-center gap-1"><Copy size={12}/> Copy link</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
